/**
 * Who is responsible for fixing a failed nearby refresh — us, or the user.
 *
 * Its own module with no imports, for the same reason as `visibility-intent.ts`:
 * the thing that broke is a SEQUENCE (fail → retry → fail → retry → give up),
 * and a sequence cannot be checked by reading the source of the screen that
 * renders its last frame. A plain `node --test` drives this one end to end.
 *
 * ## The bug this exists to prevent
 *
 * A transient request failure painted a full-screen "opncue is having trouble
 * connecting" with a Try again button, and the user's session stopped dead
 * until they pressed it. Pressing it usually worked first time — which is the
 * whole indictment. If one tap fixes it, the tap was never the user's to make.
 *
 * The rule this encodes:
 *
 *   - `auto` — nothing about the user's device or surroundings is wrong. A
 *     later identical request would very likely succeed. We retry it ourselves,
 *     behind a spinner, and say nothing.
 *   - `user`  — the app physically cannot proceed until a human does something:
 *     turn the internet on, grant location, move outside, widen their range.
 *     Retrying is theatre. Say what is wrong, immediately, and stop.
 *
 * ## Why `auto` still gives up
 *
 * Retrying forever is the other failure: a spinner that never resolves is worse
 * than an error, because it never even tells the user they are stuck. Once the
 * attempts are spent the honest reading changes — a failure that survives
 * several spaced retries is no longer plausibly transient, and by then it can
 * only be our API or their device. That is a real condition, and it gets the
 * screen it always had.
 *
 * `MAX_AUTO_ATTEMPTS` and `RETRY_DELAYS_MS` are the bound. There is no path
 * through this module that schedules an unbounded number of requests.
 */

/** Mirrors `NearbyError` in `session-store.ts`. Kept as a literal union so this module imports nothing. */
export type NearbyFailure =
  | "permission_denied"
  | "device_location_off"
  | "location_timeout"
  | "location_stale"
  | "location_imprecise"
  | "offline"
  | "backend_unavailable"
  | "request_failed";

export const NEARBY_FAILURES: readonly NearbyFailure[] = [
  "permission_denied",
  "device_location_off",
  "location_timeout",
  "location_stale",
  "location_imprecise",
  "offline",
  "backend_unavailable",
  "request_failed",
];

export type Recoverability = "auto" | "user";

/**
 * Which failures we fix ourselves.
 *
 * `location_stale` is here and `location_imprecise` deliberately is not, though
 * they arrive by the same route. A stale fix is cured by taking another
 * reading, which is precisely what the retry does. An imprecise one is not:
 * the next reading comes from the same coarse permission and lands in the same
 * place, so retrying it is a loop that ends where it started. That one needs a
 * grant or a wider range, and only the user can give either.
 *
 * `offline` is a user condition even though connectivity does come back on its
 * own, because the app cannot do anything at all until it does and silence
 * would read as the app being broken.
 */
export function recoverability(failure: NearbyFailure): Recoverability {
  switch (failure) {
    case "request_failed":
    case "backend_unavailable":
    case "location_timeout":
    case "location_stale":
      return "auto";
    case "offline":
    case "permission_denied":
    case "device_location_off":
    case "location_imprecise":
      return "user";
  }
}

/**
 * The gap before each retry, indexed by how many have already been made.
 *
 * This table is THE bound. Its length is how many attempts exist, and running
 * off the end of it is how they run out — there is deliberately no second
 * constant saying the same thing, because a mutation test found that a limit
 * expressed twice can be raised in one place with nothing failing: the other
 * copy quietly held the line and the guard meant nothing.
 *
 * Backed off rather than flat, because the failures worth retrying are the ones
 * where something needs a moment: a request that raced a token refresh wants
 * milliseconds, a restarting API wants seconds. Flat 1s retries would spend
 * every attempt inside the first second and report failure before the second
 * one had a chance to be different.
 */
export const RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000, 7_000];

/** How many times we press the button on the user's behalf. Read off the table above. */
export const MAX_AUTO_ATTEMPTS = RETRY_DELAYS_MS.length;

/**
 * The table is a CENTRE, not an appointment: each delay is spread over
 * [0.5x, 1.5x] of it.
 *
 * Without this the delays are fixed, and fixed delays after a shared failure
 * are a synchronised retry. The failures worth retrying here are exactly the
 * ones that arrive together — an API task dying takes every client in the
 * fleet at the same instant — so 2,100 devices would return at precisely
 * +1s, +3s and +7s, three times, onto the thing that just fell over. Spreading
 * is what makes a retry a retry rather than a second burst.
 *
 * A 3x span (0.5 to 1.5) rather than full jitter over [0, x] because the lower
 * half matters: retrying at ~0ms is not a backoff at all, and the first delay
 * is already only a second.
 */
export const RETRY_SPREAD_FLOOR = 0.5;
export const RETRY_SPREAD_SPAN = 1;

/**
 * The longest server-requested wait we will sit through rather than surface.
 *
 * When the server sends `Retry-After` it is not advice, it is a statement that
 * the request WILL fail until then — our own 429 carries `Retry-After: 60`,
 * because `@nestjs/throttler` blocks for `blockDuration || ttl` and the ttl is
 * 60s. Retrying inside that window is three guaranteed-refused requests per
 * client, which for a fleet is thousands of doomed requests aimed at something
 * already shedding.
 *
 * So a long ask ends the recovery instead of pacing it: show the error, which
 * the user can retry by hand when they choose.
 *
 * The number is DERIVED, not chosen to taste. `totalRecoveryWindowMs` promises
 * the user is never told nothing for more than 20s, and an honoured floor
 * replaces the table's own delay on every attempt, so the worst case is
 *
 *     sum over attempts of (FLOOR + SPAN) x max(table[i], this cap)
 *
 * At 3s that is 1.5 x (3000 + 3000 + 7000) = 19,500ms, which fits. At the 10s
 * this was first set to it is 45,000ms — the budget was silently more than
 * doubled, and `totalRecoveryWindowMs` did not know because it only summed the
 * table. Raising this without re-deriving that sum breaks the promise.
 *
 * A server asking for 3s or less is a blip we absorb. Longer than that is
 * admission control, and the honest answer to admission control is to say so.
 */
export const MAX_HONOURED_RETRY_AFTER_MS = 3_000;

export type RecoveryStep =
  | { action: "retry"; delayMs: number; attempt: number }
  | { action: "surface" };

/** What the caller knows about this particular failure, beyond its kind. */
export type RecoveryContext = {
  /**
   * `ApiError.retryAfterMs` when the server sent one. Treated as a FLOOR when
   * short, and as a reason to stop when long — see
   * `MAX_HONOURED_RETRY_AFTER_MS`.
   */
  retryAfterMs?: number;
  /** Injectable for tests. The spread is meaningless if it cannot be pinned. */
  random?: () => number;
};

/**
 * What to do about a failure, given how many retries have already been spent.
 *
 * `attempt` counts retries ALREADY made, so the first failure arrives with 0.
 * Returning `surface` is always safe — it is the behaviour this whole module
 * replaces — which is why every fall-through ends there rather than retrying.
 */
export function nextRecoveryStep(
  failure: NearbyFailure,
  attemptsMade: number,
  context: RecoveryContext = {},
): RecoveryStep {
  if (recoverability(failure) === "user") return { action: "surface" };
  if (attemptsMade < 0 || !Number.isFinite(attemptsMade)) {
    return { action: "surface" };
  }

  // A server-stated wait we cannot sit through ends the recovery rather than
  // pacing it. Checked BEFORE the table lookup: whether the ladder has budget
  // left is irrelevant when every attempt it could still make is already known
  // to be refused.
  const asked = context.retryAfterMs;
  const floorMs =
    typeof asked === "number" && Number.isFinite(asked) && asked > 0 ? asked : 0;
  if (floorMs > MAX_HONOURED_RETRY_AFTER_MS) return { action: "surface" };
  // Running off the end of the table IS the bound; see `RETRY_DELAYS_MS`. An
  // `attemptsMade >= MAX_AUTO_ATTEMPTS` line used to sit above this one, and a
  // mutant that raised its limit survived — this lookup was holding the bound
  // on its own, so the comparison was decoration that read like a guarantee.
  const base = RETRY_DELAYS_MS[attemptsMade];
  if (base === undefined) return { action: "surface" };

  const random = context.random ?? Math.random;
  // Uniform over an explicit [lo, hi], NOT a spread that is afterwards clamped
  // up to the floor.
  //
  // Clamping looks equivalent and is not. `Math.max(spread, floor)` maps every
  // draw landing below the floor ONTO the floor, so with a server ask of 2s
  // roughly half the fleet returned at exactly 2000ms — a point mass at
  // precisely the moment the server named, which is the herd this exists to
  // break. Measured at 50.3% of clients on one value before this change.
  // Choosing the interval first and drawing inside it cannot produce one,
  // because no draw is ever moved.
  //
  //   lo  never earlier than the server asked, and never earlier than our own
  //       backoff would have gone unaided.
  //   hi  1.5x whichever of the two is longer — the same quantity
  //       `totalRecoveryWindowMs` sums to get the budget.
  const lo = Math.max(base * RETRY_SPREAD_FLOOR, floorMs);
  const hi = Math.max(base, floorMs) * (RETRY_SPREAD_FLOOR + RETRY_SPREAD_SPAN);
  const delayMs = lo + random() * (hi - lo);
  return { action: "retry", delayMs, attempt: attemptsMade + 1 };
}

/**
 * Is the screen allowed to show this failure yet?
 *
 * The single question the home screen asks. A failure being RECORDED and a
 * failure being SHOWN are different events now, and conflating them is how the
 * spinner would flash an error for one frame between a failure and its retry.
 */
export function shouldSurface(
  failure: NearbyFailure,
  attemptsMade: number,
): boolean {
  return nextRecoveryStep(failure, attemptsMade).action === "surface";
}

/**
 * Total time a user can spend watching the spinner before being told anything.
 *
 * The WORST case, which is what the question is asking: the delays are spread
 * over [0.5x, 1.5x] of the table, so the sum of the table alone would describe
 * only the average user and quietly understate the one whose spinner runs
 * longest. A budget that is right on average is not a budget.
 */
export function totalRecoveryWindowMs(): number {
  return RETRY_DELAYS_MS.slice(0, MAX_AUTO_ATTEMPTS).reduce(
    // Per attempt, the longest this can take: an honoured server floor
    // REPLACES the table's delay when it is longer, so summing the table alone
    // describes a world where the server never speaks. It usually does.
    (sum, ms) =>
      sum +
      Math.max(ms, MAX_HONOURED_RETRY_AFTER_MS) *
        (RETRY_SPREAD_FLOOR + RETRY_SPREAD_SPAN),
    0,
  );
}
